import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {ProfileClaims} from "@core/models/profile-claims";
import {environment} from "@environments/environment";
import {AuthConfig, OAuthService} from "angular-oauth2-oidc";
import {BehaviorSubject, Observable} from 'rxjs';
import {UserPreferences} from '@core/models/user-preferences';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly STORAGE_KEY_TOKEN = 'token';
  private readonly STORAGE_KEY_PERMISSIONS = 'userPermissions';
  private permissions$ = new BehaviorSubject<string[] | null>(null);
  private usuarioUri = environment.api.uri.concat('usuario');
  private http = inject(HttpClient);

  constructor(private oauthService: OAuthService, private router: Router) {
    const authConfig: AuthConfig = {
      issuer:
      environment.msal.issuer,
      redirectUri: environment.msal.redirectUri,
      clientId: environment.msal.clientId,
      responseType: 'code',
      scope: 'openid profile email user.read',
      showDebugInformation: false,
      strictDiscoveryDocumentValidation: false,
      requireHttps: environment.msal.requireHttps,
    };

    this.oauthService.configure(authConfig);

    // Tenta inicializar permissões do localStorage (evita "flash" sem permissões até a resposta da API)
    const saved = localStorage.getItem(this.STORAGE_KEY_PERMISSIONS);
    if (saved) {
      this.permissions$.next(JSON.parse(saved));
    }

    // Rebusca as permissões a cada carregamento da app (ex.: F5), não só no login,
    // senão uma mudança de perfil feita pelo admin só reflete apos logout/login.
    // Adiada com queueMicrotask: chamar loadUserPermissions() direto aqui dispara o
    // authInterceptor, que injeta AuthService antes dele terminar de ser construído
    // (NG0200 circular dependency). O microtask roda só depois que o Angular termina
    // de registrar essa instância.
    if (this.isAuthenticated() && !(environment as any).local) {
      queueMicrotask(() => this.loadUserPermissions());
    }
  }

  handleLoginRedirection() {
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        // Carrega permissões do usuário
        this.loadUserPermissions();
        // ex.: "Abrir no SisNot" do SEI sem login: volta pra tela pedida
        let depois: string | null = null;
        try {
          depois = sessionStorage.getItem('sisnot:depois-do-login');
          sessionStorage.removeItem('sisnot:depois-do-login');
        } catch { /* sem storage */ }
        this.router
          // só caminho do próprio SisNot ("/..."; nunca "//outro-site")
          .navigateByUrl(depois && /^\/(?![\/\\])/.test(depois) ? depois : '/controle-notificacoes')
          .then(() => console.info('[AuthService] redirected to home'));
      } else {
        this.router
          .navigate(['/auth/login'])
          .then(() => console.info('[AuthService] redirected to login'));
      }
    });
  }

  login() {
    // dentro do painel lateral da extensão SisNot ↔ SEI (iframe): o login da
    // ARTESP não abre em quadro (X-Frame-Options DENY). Entra numa aba; depois
    // é só recarregar o painel (↻), que usa o mesmo login.
    if (window.top !== window) {
      window.open(window.location.href, '_blank', 'noopener');
      return;
    }
    console.log('[Auth] Iniciando login, issuer:', environment.msal.issuer);
    this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        console.log('[Auth] Discovery OK, iniciando code flow');
        this.oauthService.initCodeFlow();
      })
      .catch(err => {
        console.error('[Auth] Erro ao carregar discovery document:', err);
      });
  }

  logout() {
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      this.permissions$.next(null);
      localStorage.removeItem(this.STORAGE_KEY_PERMISSIONS);

      // Se o provedor de identidade expoe endpoint de logout, oauthService.logOut()
      // redireciona para la (encerra a sessao no provedor tambem). Caso contrario,
      // ele so limpa os tokens locais e nao navega para lugar nenhum sozinho.
      const hasIdpLogout = !!this.oauthService.logoutUrl;
      this.oauthService.logOut();
      if (!hasIdpLogout) {
        this.router.navigate(['/auth/login']);
      }
    });
  }

  getProfileClaims(): ProfileClaims | null {
    if ((environment as any).local && !this.oauthService.hasValidIdToken()) {
      return { name: 'Usuário Local', email: 'local@dev' };
    }
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) return null;
    return {
      name: claims['nome'],
      email: claims['email'],
      picture: claims['picture'],
    };
  }


  isAuthenticated(): boolean {
    if ((environment as any).local) return true; // LOCAL: simula autenticado para passar pelos guards
    return this.oauthService.hasValidIdToken();
  }

  getIdToken() {
    return this.oauthService.getIdToken();
  }

  getPreferences(): Observable<UserPreferences> {
    return this.http.get<UserPreferences>(`${this.usuarioUri}/preferencias`);
  }

  setPreferences(payload: UserPreferences): Observable<void> {
    return this.http.post<void>(`${this.usuarioUri}/preferencias`, payload)
  }

  loadUserPermissions() {
    this.http.get<string[]>(`${this.usuarioUri}/permissoes`).subscribe(perms => {
      this.permissions$.next(perms);
      localStorage.setItem(this.STORAGE_KEY_PERMISSIONS, JSON.stringify(perms));
    });
  }

  getPermissions() {
    return this.permissions$.asObservable();
  }

  getPermissionsValue(): string[] | null {
    return this.permissions$.getValue();
  }

  hasPermission(permission: string): boolean {
    if ((environment as any).local && !this.oauthService.hasValidIdToken()) return true;
    const perms = this.permissions$.getValue();
    return perms ? perms.includes(permission) : false;
  }

  hasAnyPermission(perms: string[]): boolean {
    if ((environment as any).local && !this.oauthService.hasValidIdToken()) return true;
    return perms.some(perm => this.hasPermission(perm));
  }
}
